<template>
  <li
    ref="root"
    class="relative flex h-full items-center first:pl-0 md:gap-2 xl:gap-4"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <SFNavigationTreeItem
      ref="trigger"
      :class="{
        'text-primary': !item.customData?.linkColor,
      }"
      :is-active="isActive"
      :navigation-item="item"
      class="!mr-0 flex !h-6 items-center !py-0 font-sans xs:!px-0 md:!px-1"
      data-testid="nav-link-main"
    >
      <span class="leading-10 md:text-md xl:text-lg" @click="closeFlyout">
        {{ item.name }}
      </span>
    </SFNavigationTreeItem>
    <SFButton
      v-if="item.children.length"
      ref="button"
      :aria-controls="`${item.id}`"
      :aria-expanded="isOpen"
      :aria-label="item.name"
      class="pointer-events-none absolute -right-4 z-10 opacity-0 focus-within:opacity-100"
      variant="raw"
      @click="openFlyout(true)"
    >
      <IconChevronDown class="size-4" />
    </SFButton>
    <div :id="`${item.id}`">
      <SFDesktopNavigationFlyout
        v-show="showFlyout"
        ref="flyout"
        :item="item"
        class="absolute inset-x-0 top-[40px] z-100 w-screen"
        :style="flyoutTransform"
        @close="closeFlyout"
      />
    </div>
  </li>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, onUnmounted } from 'vue'
import type { NavigationTreeItem as NavigationTreeItemType } from '@scayle/storefront-nuxt'
import { onKeyStroke, useElementBounding } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import SFDesktopNavigationFlyout from './SFDesktopNavigationFlyout.vue'
import { usePageState } from '~/composables'
import { isNavigationItemCategoryActive } from '#storefront-navigation/utils'
import SFNavigationTreeItem from '~/components/SFNavigationTreeItem.vue'
import { SFButton } from '~/modules/ui/runtime/components'
import { useRoute } from '#app/composables/router'

const { item } = defineProps<{
  item: NavigationTreeItemType
  isFirstItem: boolean
}>()

const { pageState } = usePageState()
const route = useRoute()

const isOpen = ref(false)
const trapFocusImmediately = ref(false)
const flyout = ref()
const showFlyout = computed(() => isOpen.value && item.children.length > 0)

// Debounce timer for hover delay
const hoverTimer = ref<NodeJS.Timeout | null>(null)
const HOVER_DELAY = 300

const { activate, deactivate } = useFocusTrap(flyout, {
  immediate: false,
  escapeDeactivates: true,
  allowOutsideClick: true,
  isKeyBackward: (keyEvent) => keyEvent.code === 'ArrowUp',
  isKeyForward: (keyEvent) => keyEvent.code === 'ArrowDown',
  onPostDeactivate() {
    isOpen.value = false
  },
  initialFocus: () => (trapFocusImmediately.value ? undefined : false),
})

const isActive = computed(() => {
  return (
    isNavigationItemCategoryActive(
      item,
      pageState.value.type,
      route.params.id as string,
    ) || isOpen.value
  )
})

const openFlyout = async (shouldTrapFocusImmediately: boolean) => {
  trapFocusImmediately.value = shouldTrapFocusImmediately
  isOpen.value = true
  await nextTick()
  activate()
}

const handleMouseEnter = () => {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
  }

  hoverTimer.value = setTimeout(() => {
    openFlyout(false)
  }, HOVER_DELAY)
}

const handleMouseLeave = () => {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
    hoverTimer.value = null
  }
  closeFlyout()
}

const closeFlyout = () => {
  isOpen.value = false
  deactivate()
}

const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
onKeyStroke(
  ARROW_KEYS,
  (event: KeyboardEvent) => {
    // Prevent scrolling the page on arrow keys
    event.preventDefault()
  },
  { target: flyout },
)

const trigger = ref()
const NAVIGATION_ITEM_SPACE_BETWEEN_IN_PX = 0
const { x } = useElementBounding(trigger, {
  windowScroll: false,
})
const flyoutTransform = computed(() => {
  return {
    transform: `translateX(-${
      x.value - NAVIGATION_ITEM_SPACE_BETWEEN_IN_PX
    }px)`,
  }
})

onUnmounted(() => {
  if (hoverTimer.value) {
    clearTimeout(hoverTimer.value)
  }
})
</script>
